// Sample code obtained from https://github.com/ivmartel/dwv-react

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';

import logger from 'utils/logger';
import Stack from '@mui/material/Stack';
import LinearProgress from '@mui/material/LinearProgress';

import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

// https://mui.com/material-ui/material-icons/
import RefreshIcon from '@mui/icons-material/Refresh';
import MenuIcon from '@mui/icons-material/Menu';
import ContrastIcon from '@mui/icons-material/Contrast';
import SearchIcon from '@mui/icons-material/Search';
import StraightenIcon from '@mui/icons-material/Straighten';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';

import Slide from '@mui/material/Slide';

import './DwvComponent.css';

import { App, decoderScripts } from 'dwv';
import { StyledToggleButton } from './styles';
import { Spinner } from 'components';

const log = logger.createLogger('DicomViewer');

// Image decoders (for web workers)
decoderScripts.jpeg2000 = `${
  import.meta.env.VITE_PUBLIC_URL
}/assets/dwv/decoders/pdfjs/decode-jpeg2000.js`;
decoderScripts['jpeg-lossless'] = `${
  import.meta.env.VITE_PUBLIC_URL
}/assets/dwv/decoders/rii-mango/decode-jpegloss.js`;
decoderScripts['jpeg-baseline'] = `${
  import.meta.env.VITE_PUBLIC_URL
}/assets/dwv/decoders/pdfjs/decode-jpegbaseline.js`;
decoderScripts.rle = `${
  import.meta.env.VITE_PUBLIC_URL
}/assets/dwv/decoders/dwv/decode-rle.js`;

export const TransitionUp = React.forwardRef((props, ref) => (
  <Slide direction="up" {...props} ref={ref} />
));

TransitionUp.displayName = 'TransitionUp';

const DicomViewer = ({ images, hideToolBar = false }) => {
  const [state, setState] = useState({
    tools: {
      Scroll: {},
      ZoomAndPan: {},
      WindowLevel: {},
      Draw: {
        options: ['Ruler'],
      },
    },
    selectedTool: 'Select Tool',
    loadProgress: 0,
    dataLoaded: false,
    dwvApp: null,
    metaData: {},
    orientation: undefined,
    showDicomTags: false,
    dropboxDivId: 'dropBox',
    dropboxClassName: 'dropBox',
    borderClassName: 'dropBoxBorder',
    hoverClassName: 'hover',
  });
  const currentImagesHashRef = useRef(null);
  const isLoadingRef = useRef(false);

  const updateState = (updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  const toolLabels = {
    Scroll: intl.get('shared_value_scroll'),
    ZoomAndPan: intl.get('shared_value_zoom_and_pan'),
    WindowLevel: intl.get('shared_value_window_and_level'),
    Draw: intl.get('shared_value_draw'),
    Reset: intl.get('shared_value_reset'),
    ToggleOrientation: intl.get('shared_value_toggle_orientation'),
  };

  const getToolIcon = useCallback((tool) => {
    if (tool === 'Scroll') return <MenuIcon fontSize="small" />;
    if (tool === 'ZoomAndPan') return <SearchIcon fontSize="small" />;
    if (tool === 'WindowLevel') return <ContrastIcon fontSize="small" />;
    if (tool === 'Draw') return <StraightenIcon fontSize="small" />;
    return null;
  }, []);

  const canRunTool = useCallback(
    (tool) => {
      if (!state.dwvApp || !state.dataLoaded) return false;
      try {
        if (tool === 'Scroll') {
          return state.dwvApp.canScroll();
        }
        if (tool === 'WindowLevel') {
          return state.dwvApp.canWindowLevel();
        }
        return true;
      } catch (_) {
        return false;
      }
    },
    [state.dwvApp, state.dataLoaded]
  );

  const onChangeShape = useCallback(
    (shape) => {
      if (state.dwvApp) {
        state.dwvApp.setToolFeatures({ shapeName: shape });
      }
    },
    [state.dwvApp]
  );

  const onChangeTool = useCallback(
    (tool) => {
      if (state.dwvApp) {
        updateState({ selectedTool: tool });
        state.dwvApp.setTool(tool);
        if (tool === 'Draw') {
          onChangeShape(state.tools.Draw.options[0]);
        }
      }
    },
    [state.dwvApp, state.tools.Draw.options, onChangeShape]
  );

  const onReset = useCallback(() => {
    if (state.dwvApp) {
      state.dwvApp.resetDisplay();
    }
  }, [state.dwvApp]);

  const toggleOrientation = useCallback(() => {
    if (!state.dwvApp) return;

    let newOrientation;
    if (state.orientation === 'axial') newOrientation = 'coronal';
    else if (state.orientation === 'coronal') newOrientation = 'sagittal';
    else if (state.orientation === 'sagittal') newOrientation = 'axial';
    else newOrientation = 'coronal'; // default case

    updateState({ orientation: newOrientation });

    const config = {
      '*': [
        {
          divId: 'viewerLayer',
          // Specify we want a single layer
          orientation: newOrientation,
        },
      ],
    };
    state.dwvApp.setDataViewConfigs(config);
    const dataIds = state.dwvApp.getDataIds();
    for (const dataId of dataIds) {
      state.dwvApp.render(dataId);
    }
  }, [state.dwvApp, state.orientation]);

  // Create a hash of images array to detect actual changes
  const createImagesHash = useCallback((imagesArray) => {
    if (!imagesArray || !Array.isArray(imagesArray)) return null;
    return imagesArray.join('|');
  }, []);

  useEffect(() => {
    const newImagesHash = createImagesHash(images);

    // Check if we're already loading the same images (using ref for synchronous check)
    if (
      isLoadingRef.current &&
      currentImagesHashRef.current === newImagesHash
    ) {
      log.warn('Already loading the same images, skipping duplicate load');
      return;
    }

    // Check if we already have the same images loaded
    if (state.dataLoaded && currentImagesHashRef.current === newImagesHash) {
      log.warn('Same images already loaded, skipping reload');
      return;
    }

    // Update refs immediately (synchronous)
    currentImagesHashRef.current = newImagesHash;
    isLoadingRef.current = true;

    //! Cleaning up existing app and layers
    updateState({
      dataLoaded: false,
      loadProgress: 0,
    });
    // Clean up existing app if it exists
    if (state.dwvApp) {
      try {
        state.dwvApp.reset();
        window.removeEventListener('resize', state.dwvApp.onResize);
      } catch (error) {
        log.warn('Error cleaning up existing app:', error);
      }
    }
    // Clean up any existing layers
    const existingLayers = document.querySelectorAll(
      '[id^="viewerLayer-layer-"]'
    );
    existingLayers.forEach((layer) => layer.remove());

    // Forcing all the images to be DICOM files
    async function urlToDicomFile(url, i = 0) {
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) throw new Error(`GET failed ${res.status}`);
      const blob = await res.blob();
      return new File([blob], `image_${i}.dcm`, { type: 'application/dicom' });
    }

    // Validate images array
    if (!images || !Array.isArray(images) || images.length === 0) {
      log.warn('No valid images provided to DicomViewer');
      return;
    }

    // Validate individual URLs
    const validImages = images.filter((url) => {
      if (!url || typeof url !== 'string') {
        log.warn('Invalid image URL:', url);
        return false;
      }
      return true;
    });

    if (validImages.length === 0) {
      log.warn('No valid image URLs found');
      return;
    }

    //! Creating a new app instance
    const app = new App();
    let isFirstRender = true;
    // eslint-disable-next-line no-unused-vars
    let nLoadItem = 0;
    let nReceivedLoadError = 0;
    let nReceivedLoadAbort = 0;

    // Initialize app
    app.init({
      dataViewConfigs: {
        '*': [{ divId: 'viewerLayer' }],
      },
      tools: state.tools,
    });

    // Use Promise.all to handle the async operations
    Promise.all(validImages.map((u, i) => urlToDicomFile(u, i)))
      .then((dicomFiles) => {
        app.loadFiles(dicomFiles);
      })
      .catch((error) => {
        log.error('Error loading DICOM files:', error);
        updateState({ loadProgress: 0 });
      });

    // Event listeners
    app.addEventListener('loadstart', () => {
      nLoadItem = 0;
      nReceivedLoadError = 0;
      nReceivedLoadAbort = 0;
      isFirstRender = true;
    });

    app.addEventListener('loadprogress', (event) => {
      updateState({ loadProgress: event.loaded });
    });

    app.addEventListener('renderend', () => {
      if (isFirstRender) {
        isFirstRender = false;
        let selectedTool = 'ZoomAndPan';
        if (app.getDataIds().length > 1) {
          selectedTool = 'Scroll';
        }
        onChangeTool(selectedTool);
      }
    });

    //! For some reason the viewer creates 2 layers, so we need to remove the first one
    app.addEventListener('load', () => {
      const layers = document.querySelectorAll('[id^="viewerLayer-layer-"]');
      if (layers.length > 1) {
        // The last layer is typically the active one with event listeners
        // Remove all but the last layer
        Array.from(layers)
          .slice(0, -1)
          .forEach((layer) => layer.remove());
      }
      updateState({
        dataLoaded: true,
      });
    });

    app.addEventListener('loadend', () => {
      if (nReceivedLoadError) {
        updateState({ loadProgress: 0 });
      }
      if (nReceivedLoadAbort) {
        updateState({ loadProgress: 0 });
      }
    });

    app.addEventListener('loadabort', (/*event*/) => {
      log.error('loadabort');
      ++nReceivedLoadAbort;
    });

    app.addEventListener('error', (event) => {
      log.error('error', event.error);
    });

    app.addEventListener('loaditem', () => {
      ++nLoadItem;
    });

    app.addEventListener('loaderror', (event) => {
      log.error('ERROR', event.error);
      ++nReceivedLoadError;
    });

    app.addEventListener('loadabort', () => {
      ++nReceivedLoadAbort;
    });

    app.addEventListener('timeout', (event) => {
      log.error('timeout', event.error);
    });

    app.addEventListener('keydown', (event) => {
      app.defaultOnKeydown(event);
    });

    window.addEventListener('resize', app.onResize);

    updateState({ dwvApp: app });

    // Cleanup
    return () => {
      window.removeEventListener('resize', app.onResize);

      // Properly destroy the app instance
      if (state.dwvApp) {
        state.dwvApp.reset();
      }
      // Clean up any remaining layers
      const remainingLayers = document.querySelectorAll(
        '[id^="viewerLayer-layer-"]'
      );
      remainingLayers.forEach((layer) => layer.remove());
    };
  }, [images]); // Empty dependency array since this should only run once

  const handleToolChange = (event, newTool) => {
    if (newTool) {
      onChangeTool(newTool);
    }
  };

  const toolsButtons = Object.keys(state.tools).map((tool) => (
    <StyledToggleButton
      color="primary"
      value={tool}
      size="large"
      key={tool}
      disabled={!state.dataLoaded || !canRunTool(tool)}>
      {getToolIcon(tool)}
      <span style={{ fontSize: '0.75rem' }}>{toolLabels[tool]}</span>
    </StyledToggleButton>
  ));

  return (
    <div style={{ width: '100%', height: '100%', backgroundColor: 'black' }}>
      {!hideToolBar && (
        <>
          <LinearProgress variant="determinate" value={state.loadProgress} />
          <Stack
            direction="row"
            spacing={1}
            padding={1}
            justifyContent="center"
            flexWrap="wrap">
            <ToggleButtonGroup
              size="medium"
              color="primary"
              value={state.selectedTool}
              exclusive
              onChange={handleToolChange}>
              {toolsButtons}
            </ToggleButtonGroup>

            <StyledToggleButton
              size="medium"
              value="reset"
              title="Reset"
              disabled={!state.dataLoaded}
              onChange={onReset}>
              <RefreshIcon fontSize="small" />
              <span style={{ fontSize: '0.75rem' }}>{toolLabels.Reset}</span>
            </StyledToggleButton>

            <StyledToggleButton
              size="medium"
              value="toggleOrientation"
              title={toolLabels.ToggleOrientation}
              disabled={!state.dataLoaded}
              onClick={toggleOrientation}>
              <CameraswitchIcon fontSize="small" />
              <span style={{ fontSize: '0.75rem' }}>
                {toolLabels.ToggleOrientation}
              </span>
            </StyledToggleButton>
          </Stack>
        </>
      )}
      {/* Due to how dwv works, this div must exist in the DOM for initialization */}
      <div
        id="viewerLayer"
        style={{
          position: 'relative',
          padding: 0,
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          height: '80%',
          visibility: state.dataLoaded ? 'visible' : 'hidden',
        }}>
        <div
          style={{
            position: 'absolute',
            top: '10%', // To account for the toolbar height
            left: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '80%',
            width: '100%',
            visibility: state.dataLoaded ? 'hidden' : 'visible',
            backgroundColor: 'rgba(0, 0, 0, 0.7)', // Opti
          }}>
          <Spinner size={70} color="white" />
        </div>
      </div>
    </div>
  );
};

DicomViewer.propTypes = {
  images: PropTypes.array,
  hideToolBar: PropTypes.bool,
};

DicomViewer.displayName = 'DicomViewer';

export default DicomViewer;
